import { isInSoftBlockMode } from 'constants/geoBlocking';
import { CLUB_LANDING_PAGE } from 'constants/pages';
import { pluralize } from 'utils/shared/pluralize';
import SafeAreaPage from 'layouts/SafeAreaPage';
import FilterIcon from 'svg/FilterIcon';
import Plus from 'svg/Plus';
import Button, { ButtonLink } from 'components/Button';
import DistanceDropdown from 'components/DiscoverPage/DistanceDropdown';
import FilterModal from 'components/DiscoverPage/FilterModal';
import Footer from 'components/Footer';
import { ExploreAllMarketPlacesTypesEnum } from 'components/DiscoverPage/props';
import MapToggleBar from 'components/Explore/MapToggleBar';
import GoogleMap from 'components/GoogleMap';
import TopNav from 'components/nav/TopNav';
import classNames from 'styles/utils/classNames';
import { ExploreCourtsProps } from './props';
import useCourt from './useCourt';

const ExploreCourts: React.FC<ExploreCourtsProps> = ({ resultsLimit, initialSport }) => {
  const {
    CourtList,
    courtMapProps,
    CourtFilters,
    clearCourtFilters,
    distance,
    setDistance,
    courtsLength,
    // UI state and handlers
    isModalOpen,
    openModal,
    closeModal,
    isOpenMap,
    handleToggleMap,
    discoverContainerRef,
    isDiscoverSectionInView,
  } = useCourt({ resultsLimit, initialSport });

  return (
    <SafeAreaPage isHideSidebar shouldHideTabBar={isOpenMap}>
      <TopNav
        shouldHideNavigation={false}
        shouldShowMobile
        shouldLinkToAuthPage={false}
        shouldShowAdditionalLinks={false}
        topNavSearch={{
          isEnabled: true,
          isSecondRowOverlay: isOpenMap,
          isSecondRowScrollable: !isOpenMap,
          defaultExploreType: ExploreAllMarketPlacesTypesEnum.Courts,
          defaultSport: initialSport,
          instant: true,
        }}
      />
      <div
        ref={discoverContainerRef}
        className={classNames(
          'relative grid w-full grid-cols-[minmax(0,1fr)_45%] grid-rows-[auto_1fr]',
          isOpenMap
            ? "[grid-template-areas:'header_header''list_map'] max-lg:h-[calc(100vh-theme(spacing.topnav-mobile))] max-lg:grid-rows-[1fr] max-lg:[grid-template-areas:'map_map']"
            : "h-full max-lg:[grid-template-areas:'header_header''list_list'] lg:min-h-[calc(100vh-theme(spacing.topnav-search))] lg:[grid-template-areas:'header_header''list_map']",
        )}
      >
        <div
          className={classNames(
            'sticky top-topnav-mobile z-10 flex flex-col gap-4 border-b border-color-border-input-lightmode bg-color-bg-lightmode-primary p-4 dark:border-color-border-input-darkmode dark:bg-color-bg-darkmode-primary lg:top-topnav-search',
            '[grid-area:header]',
            isOpenMap && 'max-lg:hidden',
          )}
        >
          <div className="flex gap-6 max-lg:flex-col max-lg:gap-ds-md lg:items-center">
            <div className="flex items-center gap-3 max-lg:justify-between">
              <h3 className="typography-product-heading-desktop max-lg:typography-product-heading-mobile shrink-0 whitespace-nowrap">
                {pluralize({ count: courtsLength, singular: 'Court' })} in this area
              </h3>
            </div>

            <div className="flex grow-[3] items-center gap-3">
              <DistanceDropdown
                distance={distance}
                handleDistanceChange={(distance) => setDistance(distance)}
                activeTabValue={ExploreAllMarketPlacesTypesEnum.Courts}
              />
            </div>

            <div className="flex items-center gap-3 max-lg:hidden">
              <Button
                onClick={() => openModal()}
                variant="inverted"
                className="w-fit shrink-0 whitespace-nowrap"
                size="md"
                iconLeft={
                  <FilterIcon className="h-4 w-4 text-color-bg-lightmode-invert dark:text-color-bg-darkmode-invert" />
                }
              >
                More filters
              </Button>
            </div>
          </div>
        </div>

        {/* List section */}
        <div
          className={classNames(
            'flex min-w-0 flex-col overflow-auto',
            '[grid-area:list]',
            isOpenMap ? 'hidden lg:flex' : 'flex',
          )}
        >
          {/* CourtList supplies its own pt-ds-xl, which spaces the cards below this CTA */}
          <div className="px-4 pt-ds-xl sm:px-ds-xl">
            <ButtonLink
              href={CLUB_LANDING_PAGE}
              variant="secondary"
              size="md"
              className="w-fit shrink-0 whitespace-nowrap"
              iconLeft={<Plus className="h-4 w-4" />}
            >
              List your venue
            </ButtonLink>
          </div>
          {CourtList}
        </div>

        {/* Map section */}
        <div
          className={classNames(
            'sticky self-start [grid-area:map]',
            isOpenMap
              ? 'block max-lg:h-full lg:top-[calc(theme(spacing.topnav-search)+77px)] lg:h-[calc(100vh-theme(spacing.topnav-search)-77px)]'
              : 'max-lg:hidden lg:top-[calc(theme(spacing.topnav-search)+77px)] lg:h-[calc(100vh-theme(spacing.topnav-search)-77px)]',
          )}
        >
          <GoogleMap {...courtMapProps} softBlockPlaceholder={isInSoftBlockMode()} />
        </div>
      </div>

      <MapToggleBar
        isOpenMap={isOpenMap}
        onToggleMap={handleToggleMap}
        isInView={isDiscoverSectionInView}
        onOpenFilters={() => openModal()}
      />

      <FilterModal
        title={'Filters'}
        isOpen={isModalOpen}
        closeModal={closeModal}
        clearAll={clearCourtFilters}
        closeButtonText={`Show ${courtsLength} Courts`}
      >
        {CourtFilters}
      </FilterModal>

      <Footer />
    </SafeAreaPage>
  );
};

export default ExploreCourts;
